import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface SaasDashboardData {
  readonly tenants: {
    readonly total: number;
    readonly byStatus: Record<string, number>;
    readonly byType: Record<string, number>;
    readonly byPlan: Record<string, number>;
    readonly active: number;
    readonly suspended: number;
  };
  readonly subscriptions: { readonly active: number; readonly expiringSoon: number };
  readonly domains: { readonly total: number; readonly verified: number; readonly pending: number; readonly failed: number };
  readonly features: { readonly enabled: number; readonly disabled: number };
  readonly backups: { readonly completed: number; readonly failed: number };
}

/**
 * M43 §27 SaaS Administration Dashboard. The operator's estate rollup — tenant
 * counts by status / type / plan, active + expiring subscriptions, domain
 * verification posture, feature entitlement spread and backup health — read from
 * `/v1/saas/governance/dashboard`. Viewing needs SAAS_TENANT_ADMIN TENANT_VIEW.
 */
export function SaaSAdministrationDashboard(): ReactNode {
  const { token } = useAuth();
  const [data, setData] = useState<SaasDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: SaasDashboardData }>('/v1/saas/governance/dashboard', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="saas-admin-dashboard">
      <h1>SaaS Administration Dashboard</h1>
      {error && <p role="alert" aria-label="dashboard-error">{error}</p>}
      {loading ? <p>Loading…</p> : data && (
        <>
          <section aria-label="tenant-tiles">
            <h2>Tenants</h2>
            <ul>
              <li aria-label="tile-tenant-total">Total: {data.tenants.total}</li>
              <li aria-label="tile-tenant-active">Active: {data.tenants.active}</li>
              <li aria-label="tile-tenant-suspended">Suspended: {data.tenants.suspended}</li>
            </ul>
            <table aria-label="tenant-by-status">
              <thead><tr><th scope="col">Status</th><th scope="col">Count</th></tr></thead>
              <tbody>
                {Object.entries(data.tenants.byStatus).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td aria-label={`status-count-${k}`}>{v}</td></tr>
                ))}
              </tbody>
            </table>
            <table aria-label="tenant-by-plan">
              <thead><tr><th scope="col">Plan</th><th scope="col">Count</th></tr></thead>
              <tbody>
                {Object.entries(data.tenants.byPlan).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td aria-label={`plan-count-${k}`}>{v}</td></tr>
                ))}
              </tbody>
            </table>
          </section>

          <section aria-label="subscription-tiles">
            <h2>Subscriptions</h2>
            <ul>
              <li aria-label="tile-sub-active">Active: {data.subscriptions.active}</li>
              <li aria-label="tile-sub-expiring">Expiring soon: {data.subscriptions.expiringSoon}</li>
            </ul>
          </section>

          <section aria-label="domain-tiles">
            <h2>Domains</h2>
            <ul>
              <li aria-label="tile-domain-total">Total: {data.domains.total}</li>
              <li aria-label="tile-domain-verified">Verified: {data.domains.verified}</li>
              <li aria-label="tile-domain-pending">Pending: {data.domains.pending}</li>
              <li aria-label="tile-domain-failed">Failed: {data.domains.failed}</li>
            </ul>
          </section>

          <section aria-label="feature-tiles">
            <h2>Features</h2>
            <ul>
              <li aria-label="tile-feature-enabled">Enabled: {data.features.enabled}</li>
              <li aria-label="tile-feature-disabled">Disabled: {data.features.disabled}</li>
            </ul>
          </section>

          <section aria-label="backup-tiles">
            <h2>Backups</h2>
            <ul>
              <li aria-label="tile-backup-completed">Completed: {data.backups.completed}</li>
              <li aria-label="tile-backup-failed">Failed: {data.backups.failed}</li>
            </ul>
          </section>
        </>
      )}
    </main>
  );
}
